import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield,
  Loader2,
  AlertCircle,
  Copy,
  Check,
  ArrowRight,
  Smartphone,
  Key,
} from "lucide-react";
import QRCode from "qrcode";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { OtpInput } from "./OtpInput";

type SetupStep = "qr" | "verify" | "backup";

interface MfaSetupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  secretCode: string;
  email: string;
  onVerify: (code: string) => Promise<string[] | null>;
  onComplete: () => void;
  isLoading?: boolean;
  error?: string | null;
}

export function MfaSetupDialog({
  open,
  onOpenChange,
  secretCode,
  email,
  onVerify,
  onComplete,
  isLoading = false,
  error = null,
}: MfaSetupDialogProps) {
  const [step, setStep] = useState<SetupStep>("qr");
  const [code, setCode] = useState("");
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  useEffect(() => {
    if (secretCode && email) {
      const issuer = "Echopeak Labs";
      const otpAuthUrl = `otpauth://totp/${encodeURIComponent(
        issuer
      )}:${encodeURIComponent(email)}?secret=${secretCode}&issuer=${encodeURIComponent(
        issuer
      )}&algorithm=SHA1&digits=6&period=30`;

      QRCode.toDataURL(otpAuthUrl, {
        width: 200,
        margin: 2,
        color: {
          dark: "#000000",
          light: "#ffffff",
        },
      }).then(setQrCodeUrl);
    }
  }, [secretCode, email]);

  const handleVerify = async () => {
    if (code.length !== 6) return;
    setVerifying(true);
    setVerifyError(null);

    try {
      const codes = await onVerify(code);
      if (codes) {
        setBackupCodes(codes);
        setStep("backup");
      }
    } catch (err) {
      setVerifyError(
        err instanceof Error ? err.message : "Verification failed"
      );
    } finally {
      setVerifying(false);
    }
  };

  const handleCopyBackupCodes = async () => {
    const codesText = backupCodes.join("\n");
    await navigator.clipboard.writeText(codesText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleComplete = () => {
    onComplete();
    onOpenChange(false);
  };

  const renderStep = () => {
    switch (step) {
      case "qr":
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center">
              <div className="mx-auto mb-4 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Smartphone className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-2">
                Set Up Authenticator App
              </h3>
              <p className="text-sm text-muted-foreground">
                Scan this QR code with your authenticator app (Google
                Authenticator, Authy, etc.)
              </p>
            </div>

            <div className="flex justify-center">
              {qrCodeUrl ? (
                <div className="p-4 bg-white rounded-xl shadow-lg">
                  <img src={qrCodeUrl} alt="MFA QR Code" className="w-48 h-48" />
                </div>
              ) : (
                <div className="w-48 h-48 flex items-center justify-center bg-muted rounded-xl">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <p className="text-xs text-center text-muted-foreground">
                Can't scan? Enter this code manually:
              </p>
              <div className="flex items-center justify-center gap-2">
                <code className="px-3 py-2 bg-muted rounded-lg font-mono text-sm break-all select-all">
                  {secretCode}
                </code>
              </div>
            </div>

            <Button onClick={() => setStep("verify")} className="w-full h-12">
              Continue
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>
        );

      case "verify":
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center">
              <div className="mx-auto mb-4 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Shield className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Verify Setup</h3>
              <p className="text-sm text-muted-foreground">
                Enter the 6-digit code from your authenticator app to verify the
                setup
              </p>
            </div>

            <OtpInput
              value={code}
              onChange={setCode}
              disabled={verifying || isLoading}
              error={!!verifyError || !!error}
              autoFocus
            />

            {(verifyError || error) && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm"
              >
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{verifyError || error}</span>
              </motion.div>
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setStep("qr")}
                className="flex-1 h-12"
                disabled={verifying || isLoading}
              >
                Back
              </Button>
              <Button
                onClick={handleVerify}
                disabled={code.length !== 6 || verifying || isLoading}
                className="flex-1 h-12"
              >
                {verifying || isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  "Verify"
                )}
              </Button>
            </div>
          </motion.div>
        );

      case "backup":
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center">
              <div className="mx-auto mb-4 w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center">
                <Key className="w-8 h-8 text-emerald-500" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Save Backup Codes</h3>
              <p className="text-sm text-muted-foreground">
                Save these codes in a safe place. You can use them to access
                your account if you lose access to your authenticator app.
              </p>
            </div>

            <div className="relative">
              <div className="absolute right-2 top-2 z-10">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopyBackupCodes}
                  className="h-8 px-2"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 mr-1 text-emerald-500" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-1" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
              <div className="bg-muted/50 rounded-lg p-4 pt-12 border border-border">
                <div className="grid grid-cols-2 gap-2">
                  {backupCodes.map((code, index) => (
                    <code
                      key={index}
                      className="px-3 py-2 bg-background rounded font-mono text-sm text-center"
                    >
                      {code}
                    </code>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 text-sm">
              <p className="font-medium mb-1">⚠️ Important</p>
              <p className="text-xs">
                Each backup code can only be used once. Store them securely and
                don't share them with anyone.
              </p>
            </div>

            <Button onClick={handleComplete} className="w-full h-12">
              I've Saved My Codes
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
          <DialogDescription>
            Secure your account with an authenticator app
          </DialogDescription>
        </DialogHeader>

        <AnimatePresence mode="wait">{renderStep()}</AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}

